import {useEffect, useState} from 'react';

import {featureService} from '@/services/featureService';

// One request per code per page load; every caller shares the answer. Saving a
// feature reloads the page, so a change made here is picked up by the reload.
const requests = new Map<string, Promise<boolean>>();

const loadStatus = (code: string): Promise<boolean> => {
  let request = requests.get(code);
  if (!request) {
    request = featureService.getFeatureStatus(code);
    requests.set(code, request);
    // A failed check is not kept, so the next mount asks again.
    request.catch(() => requests.delete(code));
  }
  return request;
};

/**
 * Whether each feature is switched on for anyone, keyed by code. When a check
 * fails, `hasFailed` is set and nothing is reported as on.
 */
export const useActiveFeatures = (
  codes: readonly string[],
): {active: Set<string>; hasLoaded: boolean; hasFailed: boolean} => {
  const key = codes.join(',');
  const [state, setState] = useState<{
    key: string;
    active: Set<string>;
    hasFailed: boolean;
  } | null>(null);

  useEffect(() => {
    let isCurrent = true;
    const list = key ? key.split(',') : [];
    Promise.all(list.map(loadStatus))
      .then((results) => {
        if (isCurrent) {
          setState({
            key,
            active: new Set(list.filter((_, index) => results[index])),
            hasFailed: false,
          });
        }
      })
      .catch(() => {
        if (isCurrent) {
          setState({key, active: new Set(), hasFailed: true});
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [key]);

  return state?.key === key
    ? {active: state.active, hasLoaded: true, hasFailed: state.hasFailed}
    : {active: new Set(), hasLoaded: !key, hasFailed: false};
};

export const useFeatureActive = (code: string) => {
  const {active, hasLoaded, hasFailed} = useActiveFeatures([code]);
  return {isActive: active.has(code), hasLoaded, hasFailed};
};
